import { useNavigate } from 'react-router-dom';
import { MapPin, Buildings, GraduationCap, ArrowUpRight } from '@phosphor-icons/react';

interface AlumniCardData {
  donor_id: string;
  name: string;
  batch?: string;
  department?: string;
  company?: string;
  designation?: string;
  work_city?: string;
  cumulative_giving_to_iitm?: string | number;
  recommended_ask_inr?: string | number;
  net_worth?: string | number;
}

function formatCurrency(val: string | number | undefined): string {
  const n = typeof val === 'string' ? parseFloat(val.replace(/[^0-9.-]/g, '')) : Number(val);
  if (!n || isNaN(n)) return '—';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(0)}K`;
  return `₹${n}`;
}

export function AlumniCard({ data }: { data: AlumniCardData }) {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(`/alumni/${data.donor_id}`)}
      className="group w-full text-left bg-white border border-slate-100 rounded-2xl p-4 hover:border-accent/30 hover:shadow-card-hover transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-slate-900 truncate">{data.name}</p>
          {data.designation && data.company && (
            <p className="text-xs text-slate-500 truncate mt-0.5">
              {data.designation} · {data.company}
            </p>
          )}
        </div>
        <ArrowUpRight
          size={14}
          className="text-slate-300 group-hover:text-accent transition-colors shrink-0 mt-0.5"
        />
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400 mb-3">
        {data.batch && (
          <span className="flex items-center gap-1">
            <GraduationCap size={11} />
            {data.batch}
          </span>
        )}
        {data.work_city && (
          <span className="flex items-center gap-1">
            <MapPin size={11} />
            {data.work_city}
          </span>
        )}
        {data.department && (
          <span className="flex items-center gap-1">
            <Buildings size={11} />
            {data.department}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Giving', val: formatCurrency(data.cumulative_giving_to_iitm) },
          { label: 'Ask', val: formatCurrency(data.recommended_ask_inr) },
          { label: 'Net Worth', val: formatCurrency(data.net_worth) },
        ].map(({ label, val }) => (
          <div key={label} className="bg-slate-50 rounded-xl px-2 py-1.5 text-center">
            <p className="text-[10px] text-slate-400 leading-none mb-1">{label}</p>
            <p className="text-xs font-semibold text-slate-700 leading-none">{val}</p>
          </div>
        ))}
      </div>
    </button>
  );
}

export function AlumniCardGrid({ items }: { items: AlumniCardData[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
      {items.map((item) => (
        <AlumniCard key={item.donor_id} data={item} />
      ))}
    </div>
  );
}
